import { skipToken, useMutation } from '@apollo/client'
import { Loader } from 'lucide-react'
import { Suspense, useEffect, useRef, useTransition } from 'react'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import { ToasterContent } from '@/components/sv/toaster-content'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { gql } from '@/lib/graphql/gql'
import { useIdCompany, useRBAC, useSuspenseQuery } from '@/lib/hooks'
import {
  canCompanyDeveloperManageCapabilities,
  isCompanyDeveloper,
} from '@/lib/rbac'
import { CancelInvitationDialogFallback } from '@/routes/user-management/cancel-invitation-dialog/fallback'
import type {
  CancelInvitationMutation,
  CancelInvitationMutationVariables,
  CompanyInvitationQuery,
  CompanyInvitationQueryVariables,
} from '@/routes/user-management/cancel-invitation-dialog/index.types.gen'

const QUERY_COMPANY_INVITATION = gql`
  query CompanyInvitation($idCompany: String!) {
    company(id: $idCompany) {
      id
      pendingInvitations {
        id
        capabilities {
          id
          idApp
          idDatabase
          idStaticSite
          idObjectStorage
          idLoadBalancer
          idProject
          idRole
          type
        }
      }
    }
  }
`

const CANCEL_INVITATION = gql`
  mutation CancelInvitation($id: String!) {
    cancelInvitation(idInvitation: $id)
  }
`

type Props = {
  isOpen: boolean
  onClose: () => void
}

const DialogContent = ({ isOpen, onClose }: Props) => {
  const { idInvitation = '' } = useParams<{ idInvitation: string }>()
  const { idCompany } = useIdCompany()
  const { rolesInCompany } = useRBAC()
  const [isPending, startTransition] = useTransition()
  const closeReasonRef = useRef<'blocked' | 'missing' | null>(null)
  const isCompanyDeveloperViewer = isCompanyDeveloper(rolesInCompany)

  const [cancelInvitation] = useMutation<
    CancelInvitationMutation,
    CancelInvitationMutationVariables
  >(CANCEL_INVITATION)

  const { data } = useSuspenseQuery<
    CompanyInvitationQuery,
    CompanyInvitationQueryVariables
  >(QUERY_COMPANY_INVITATION, isOpen ? { variables: { idCompany } } : skipToken)

  const invitation = data?.company?.pendingInvitations.find(
    (pendingInvitation) => pendingInvitation.id === idInvitation
  )
  const isBlockedForCompanyDeveloper =
    isCompanyDeveloperViewer &&
    !canCompanyDeveloperManageCapabilities(invitation?.capabilities ?? [])
  const closeReason = !isOpen
    ? null
    : !idInvitation || !invitation
      ? 'missing'
      : isBlockedForCompanyDeveloper
        ? 'blocked'
        : null

  useEffect(() => {
    if (!closeReason) {
      closeReasonRef.current = null
      return
    }

    if (closeReasonRef.current === closeReason) {
      return
    }

    closeReasonRef.current = closeReason
    toast(
      <ToasterContent variant="error">
        {closeReason === 'blocked'
          ? 'Company developers can only cancel project-level or service-level invitations'
          : 'Invitation not found'}
      </ToasterContent>,
      { position: 'top-right' }
    )
    onClose()
  }, [closeReason, onClose])

  if (closeReason) {
    return null
  }

  return (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle>Cancel invitation</AlertDialogTitle>
        <AlertDialogDescription>
          This action cannot be undone. This will permanently delete the
          invitation and the user will not be able to join the company.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel disabled={isPending} onClick={onClose}>
          Cancel
        </AlertDialogCancel>
        <AlertDialogAction
          disabled={isPending}
          onClick={(event) => {
            event.preventDefault()
            startTransition(async () => {
              try {
                await cancelInvitation({ variables: { id: idInvitation } })
                toast(
                  <ToasterContent variant="success">
                    Invitation cancelled successfully
                  </ToasterContent>,
                  { position: 'top-right' }
                )
                onClose()
              } catch {
                toast(
                  <ToasterContent variant="error">
                    Failed to cancel invitation
                  </ToasterContent>,
                  { position: 'top-right' }
                )
              }
            })
          }}
        >
          {isPending ? <Loader className="animate-spin" /> : null}
          Continue
        </AlertDialogAction>
      </AlertDialogFooter>
    </>
  )
}

export const CancelInvitationDialog = ({ isOpen, onClose }: Props) => {
  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <Suspense fallback={<CancelInvitationDialogFallback />}>
          <DialogContent isOpen={isOpen} onClose={onClose} />
        </Suspense>
      </AlertDialogContent>
    </AlertDialog>
  )
}
